import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PageHeader from '@/components/PageHeader';
import { WithRouter } from '@/components/WithRouter';
import { objToSearch } from '@/utils/helpers';

import { fetchStoreList } from '@/redux/dashboard/storeList/storeSlice';
import { fetchCompletionRate } from '@/redux/completionRate/completionRateSlice';
import { Card, Progress, Tooltip, Pagination, Spin } from 'antd';

import CompletionRateDrawer from '../share/CompletionRateDrawer';
import { SelectC } from '@/i2antdV5';
import { COMPLETION_RATE_CONFIG } from '../config';
import { renderTextStr } from '@/utils/renderUtils';

/**
 * dataKey 到 serviceType 的映射
 * 用于动态选择不同的 completionRate API
 */
const DATAKEY_TO_SERVICETYPE_MAP = {
  postpartumCare: 'maternityCare', // 产康当日完成率
  nursingCare: 'nursing', // 护理部当日完成率
  housekeeping: 'housekeeping', // 客房当日完成率
  catering: 'catering', // 餐饮当日完成率
  homeService: 'homeBabyCare' // 到家当日完成率
};

export class CompletionRateAnalyticsList extends React.Component {
  constructor(props) {
    super(props);
  }

  /**
   * 获取配置对象
   * @returns {Object|null} 对应的配置对象，如果未找到则返回 null
   */
  getConfig() {
    const { location } = this.props;
    if (!location || !location.search || !location.search.dataKey) {
      return null;
    }
    
    const dataKey = location.search.dataKey;
    return COMPLETION_RATE_CONFIG.find(config => config.dataKey === dataKey) || null;
  }

  /**
   * 根据 dataKey 获取对应的 serviceType
   * @returns {string|null} serviceType，如果未找到则返回 null
   */
  getServiceType() {
    const { location } = this.props;
    if (!location || !location.search || !location.search.dataKey) {
      return null;
    }
    const dataKey = location.search.dataKey;
    return DATAKEY_TO_SERVICETYPE_MAP[dataKey] || null;
  }

  /**
   * 获取面包屑列表
   * @returns {Array} 面包屑列表
   */
  getBreadcrumbList() {
    const config = this.getConfig();
    const title = config ? config.title : '服务完成率详情';
    
    return [
      { breadcrumbName: 'dashboard', href: '/client/commandCenter' },
      { breadcrumbName: title }
    ];
  }

  /**
   * 获取查询参数（排除 dataKey）
   * @returns {Object} 查询参数对象
   */
  getQuery() {
    const { location } = this.props;
    if (!location || !location.search) {
      return {};
    }
    
    const query = { ...location.search };
    // 排除 dataKey
    delete query.dataKey;
    
    return query;
  }

  /**
   * 更新 URL 并重新获取数据
   * @param {Object} newQuery - 新的查询对象
   */
  updateUrlAndFetch = (newQuery) => {
    const { location, navigate } = this.props;
    navigate(`${location.pathname}${objToSearch(newQuery)}`, { replace: true });
    
    // 重新获取数据，传入新的 query（排除 dataKey）
    const queryForFetch = { ...newQuery };
    delete queryForFetch.dataKey;
    this.fetchData(queryForFetch);
  };

  /**
   * 更新查询参数
   * @param {string|Object} keyOrUpdates - 查询键或更新对象
   * @param {*} value - 查询值（当 keyOrUpdates 为字符串时）
   */
  updateQuery = (keyOrUpdates, value) => {
    const { location } = this.props;
    const currentSearch = location.search || {};
    const newQuery = { ...currentSearch };
    
    if (typeof keyOrUpdates === 'object') {
      // 批量更新
      Object.keys(keyOrUpdates).forEach((key) => {
        if (
          keyOrUpdates[key] !== undefined &&
          keyOrUpdates[key] !== null &&
          keyOrUpdates[key] !== ''
        ) {
          newQuery[key] = keyOrUpdates[key];
        } else {
          delete newQuery[key];
        }
      });
    } else {
      // 单个更新
      if (value !== undefined && value !== null && value !== '') {
        newQuery[keyOrUpdates] = value;
      } else {
        delete newQuery[keyOrUpdates];
      }
    }
    
    this.updateUrlAndFetch(newQuery);
  };

  /**
   * 分页切换
   * @param {number} page - 页码
   * @param {number} pageSize - 每页条数
   */
  handlePageChange = (page, pageSize) => {
    const { location } = this.props;
    const currentSearch = location.search || {};
    const newQuery = {
      ...currentSearch,
      pageNumber: page,
      pageSize: pageSize,
    };
    
    this.updateUrlAndFetch(newQuery);
  };

  componentDidMount() {
    const { dispatch } = this.props;
    dispatch(fetchStoreList({
      serviceType: 'default',
      dataType: 'storeOption'
    }));
    this.fetchData();
  }

  componentWillUnmount() {}

  /**
   * 解析逾期率数值
   * @param {string|number} overdueRate - 逾期率（可能是百分比字符串如 "20%" 或数字如 20）
   * @returns {number} 逾期率数值
   */
  parseOverdueRate = (overdueRate) => {
    if (typeof overdueRate === 'string') {
      return parseFloat(overdueRate.replace('%', '')) || 0;
    }
    if (typeof overdueRate === 'number') {
      return overdueRate;
    }
    return 0;
  };

  /**
   * 根据逾期率返回卡片颜色配置
   * @param {string|number} overdueRate - 逾期率（可能是百分比字符串如 "20%" 或数字如 20）
   * @returns {Object} 包含背景色、边框色等样式配置
   */
  getOverdueRateStyle = (overdueRate) => {
    const rate = this.parseOverdueRate(overdueRate);

    // 0%-39%: 绿色系（低风险）
    if (rate >= 0 && rate < 40) {
      return {
        backgroundColor: 'rgba(82, 196, 26, 0.08)',
        borderLeft: '4px solid #52c41a',
        borderColor: '#52c41a',
      };
    }

    // 40%-79%: 橙色系（中风险）
    if (rate >= 40 && rate < 80) {
      return {
        backgroundColor: 'rgba(250, 173, 20, 0.08)',
        borderLeft: '4px solid #faad14',
        borderColor: '#faad14',
      };
    }

    // 80%-100%: 红色系（高风险）
    return {
      backgroundColor: 'rgba(255, 77, 79, 0.1)',
      borderLeft: '4px solid #ff4d4f',
      borderColor: '#ff4d4f',
    };
  };
  
  /**
   * 获取数据
   * @param {Object} query - 可选的查询参数，如果不传则使用 URL 中的参数
   */
  fetchData = (query) => {
    const { dispatch, location: { search } } = this.props;
    if (!search.dataKey) {
      console.warn('CompletionRateAnalyticsList: 未找到有效的 serviceType');
      return;
    }

    const finalQuery = query || this.getQuery();
    dispatch(fetchCompletionRate({
      serviceType: search.dataKey,
      query: finalQuery,
    }));
  };

  render() {
    const { storeObj, completionRateObj } = this.props;
    const { default_storeOption: storeOption = [] } = storeObj || {};

    const breadcrumbList = this.getBreadcrumbList();
    const serviceType = this.getServiceType();

    const defaultRenderData = {
      data: [],

      total: 0,
      pageNumber: 1,
      pageSize: 20,
    };
    const renderData =
      (serviceType && completionRateObj[serviceType]) ||
      completionRateObj.data ||
      defaultRenderData;
    const query = completionRateObj.query || this.getQuery();
    const fetching = completionRateObj.fetching || false;
    return (
      <>
        <div>
          <PageHeader breadcrumbList={breadcrumbList} />
        </div>
        <div className='i2-container'>
          <Card className="i2-card-page">
            <Spin spinning={fetching}>
              <section className="i2-app-toolbar">
                <div className="toolbar-filters">
                  <SelectC
                    label=""
                    query={query}
                    searchKey="store_id"
                    options={storeOption}
                    action={this.updateQuery}
                  />
                </div>
              </section>
              <Card>
                {renderData.data && renderData.data.length > 0 ? (
                  (renderData.data || []).map((item) => {
                    const total = item.totalServicesScheduled || 0;
                    const completed = item.totalServicesCompleted || 0;
                    const percent =
                      total > 0 ? Math.round((completed / total) * 100) : 0;
                    const overdueRateStyle = this.getOverdueRateStyle(
                      item.overdue_rate
                    );
                    return (
                      <Card.Grid
                        key={item.member_id}
                        style={{
                          width: 300,
                          textAlign: 'center',
                          padding: '16px',
                          backgroundColor: overdueRateStyle.backgroundColor,
                          borderLeft: overdueRateStyle.borderLeft,
                          border: `1px solid ${overdueRateStyle.borderColor}`,
                          borderRadius: '4px',
                          margin: '8px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        }}
                      >
                        <CompletionRateDrawer
                          title={renderTextStr(item.member_name)}
                          baseProps={{
                            data: item,
                            query: query,
                            serviceType: serviceType,
                          }}
                        >
                          <div>
                            <div style={{ marginBottom: '12px' }}>
                              <div
                                style={{
                                  fontSize: '16px',
                                  fontWeight: 'bold',
                                  color: '#333',
                                  marginBottom: '8px',
                                }}
                              >
                                {renderTextStr(item.member_name)}
                              </div>
                              <div
                                style={{
                                  fontSize: '14px',
                                  color: '#666',
                                  marginBottom: '12px',
                                }}
                              >
                                逾期: {item.overdue_count} / {item.overdue_rate}
                              </div>
                            </div>
                            <Tooltip
                              title={
                                <div>
                                  <div>
                                    已完成数: {item.totalServicesCompleted || 0}
                                  </div>
                                  <div>
                                    总数: {item.totalServicesScheduled || 0}
                                  </div>
                                </div>
                              }
                            >
                              <Progress
                                percent={percent}
                                strokeColor={{
                                  '0%': '#108ee9',
                                  '100%': '#87d068',
                                }}
                                trailColor="#e8e8e8"
                                showInfo={true}
                                format={(percent) => `${percent}%`}
                              />
                            </Tooltip>
                          </div>
                        </CompletionRateDrawer>
                      </Card.Grid>
                    );
                  })
                ) : (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '40px',
                      color: '#999',
                      width: '100%',
                    }}
                  >
                    暂无数据
                  </div>
                )}
              </Card>
              {renderData.total > 0 && (
                <div style={{ marginTop: 16, textAlign: 'right' }}>
                  <Pagination
                    size="small"
                    current={renderData.pageNumber || 1}
                    pageSize={renderData.pageSize || 20}
                    total={renderData.total || 0}
                    showSizeChanger
                    showTotal={(t) =>
                      `第 ${renderData.pageNumber || 1} 页，每页 ${
                        renderData.pageSize || 20
                      } 条，共 ${t} 条`
                    }
                    onChange={(page, size) => this.handlePageChange(page, size)}
                    onShowSizeChange={(page, size) => this.handlePageChange(1, size)}
                  />
                </div>
              )}
            </Spin>
          </Card>
        </div>
      </>
    )
  }
}

const CompletionRateAnalyticsListWrapper = (props) => {
  const dispatch = useDispatch();
  const allProps = {
    ...props, // 包含 WithRouter 注入的 location, navigate, params
    dispatch,
    storeObj: useSelector((state) => state.dashboard.storeList || {}),
    completionRateObj: useSelector(
      (state) => state.completionRate.completionRate || {}
    ),
  };
  return <CompletionRateAnalyticsList {...allProps} />;
};

export default WithRouter(CompletionRateAnalyticsListWrapper);